/**
 * StudyDocumentsCard — shared "Study Documents" panel (source-of-truth plan §3.7).
 *
 * Given a protocolId, renders the study's key-document slots (role, name,
 * version, updated date, extraction status), a View action (short-TTL
 * presigned URL via /api/registry), an "All documents" expander for
 * non-slotted docs (read-only), and a "Manage in eReg →" deep link (or the
 * ask-an-admin line when the caller lacks eReg access). Reads ONLY /api/registry
 * — uploads and edits always happen in eReg.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, ExternalLink, Loader2, ChevronDown, ChevronRight, RefreshCw, Eye,
} from 'lucide-react';
import api from '../services/api';
import { iconBtnCls, secondaryBtnCls } from './styles';
import { eregStudyLink } from './returnTo';
import { STUDY_ROLE_LABELS, openPresigned } from './studyRoles';

interface SlotDocument {
  id: string;
  document_name: string;
  document_type: string;
  version_number: string;
  status: string;
  updated_at?: string;
  deleted_at?: string | null;
}

interface KeyDocumentSlot {
  id: string;
  role: string;
  variant_label: string;
  document_id: string;
  set_at: string;
  document?: SlotDocument | null;
}

interface RegistryDocumentMeta {
  id: string;
  document_name: string;
  document_type: string;
  version_number: string;
  status: string;
  updated_at?: string;
  slots: { role: string; variant_label: string }[];
  current_version?: {
    id: string;
    version_number: string;
    created_at: string;
    extraction_status: string | null;
  } | null;
}

function extractionPill(status: string | null | undefined) {
  if (!status || status === 'complete') return null;
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    processing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    unsupported: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  };
  const labels: Record<string, string> = {
    pending: 'Text: queued',
    processing: 'Text: extracting…',
    failed: 'Text: failed',
    unsupported: 'Text: n/a',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || styles.unsupported}`}>
      {labels[status] || status}
    </span>
  );
}

interface StudyDocumentsCardProps {
  protocolId: string;
  title?: string;
  /** Caller's knowledge of eReg access — false renders the ask-an-admin line. */
  canManageInEreg?: boolean;
  /** return_to for the Manage deep link (usually location.pathname + search). */
  returnTo?: string;
  returnLabel?: string;
  /**
   * Read-API base (default the platform registry API). CRM passes its proxy
   * ('/api/crm/registry') — its tenant rule spans the whole network regardless
   * of the caller's per-site grants (plan §3.4); the proxy mirrors the
   * /studies/:id/documents and /documents/:id/file contracts.
   */
  apiBase?: string;
  className?: string;
}

export default function StudyDocumentsCard({
  protocolId,
  title = 'Study Documents',
  canManageInEreg = true,
  returnTo,
  returnLabel,
  apiBase = '/api/registry',
  className = '',
}: StudyDocumentsCardProps) {
  const [slots, setSlots] = useState<KeyDocumentSlot[]>([]);
  const [documents, setDocuments] = useState<RegistryDocumentMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${apiBase}/studies/${protocolId}/documents`);
      setSlots(res.data?.slots || []);
      setDocuments(res.data?.documents || []);
    } catch (err: any) {
      setError(err?.response?.status === 404
        ? 'Study not found'
        : 'Could not load study documents');
    } finally {
      setLoading(false);
    }
  }, [protocolId, apiBase]);

  useEffect(() => { load(); }, [load]);

  const slotDocIds = useMemo(() => new Set(slots.map((s) => s.document_id)), [slots]);
  const otherDocs = useMemo(
    () => documents.filter((d) => !slotDocIds.has(d.id) && d.status !== 'archived'),
    [documents, slotDocIds]
  );
  const docMetaById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);

  const view = async (documentId: string) => {
    setViewingId(documentId);
    try {
      await openPresigned(async () => {
        const res = await api.get(`${apiBase}/documents/${documentId}/file`);
        return res.data.url;
      });
    } finally {
      setViewingId(null);
    }
  };

  const manageLink = eregStudyLink(protocolId, { returnTo, returnLabel });

  return (
    <div className={`rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 p-4 dark:border-gray-700">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            aria-label="Refresh study documents"
            className={iconBtnCls}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {canManageInEreg ? (
            <Link
              to={manageLink}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
            >
              Manage in eReg
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Documents are managed in eReg — ask an eReg admin
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 p-6 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
          Loading documents…
        </div>
      ) : error ? (
        <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">{error}</div>
      ) : (
        <>
          {slots.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-8 text-center">
              <FileText className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No key documents pinned yet</p>
              <p className="mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">
                Upload the protocol and ICF in eReg — key documents are pinned there and every tool reads the current version.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {slots.map((slot) => {
                const doc = slot.document;
                const meta = docMetaById.get(slot.document_id);
                if (!doc || doc.deleted_at) return null;
                return (
                  <li key={slot.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-400">
                          {STUDY_ROLE_LABELS[slot.role] || slot.role}
                          {slot.variant_label !== 'default' ? ` · ${slot.variant_label}` : ''}
                        </span>
                        {extractionPill(meta?.current_version?.extraction_status)}
                      </div>
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{doc.document_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        v{doc.version_number}
                        {doc.updated_at ? ` · updated ${new Date(doc.updated_at).toLocaleDateString()}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => view(slot.document_id)}
                      disabled={viewingId === slot.document_id}
                      className={secondaryBtnCls}
                    >
                      {viewingId === slot.document_id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Eye className="h-4 w-4" />}
                      View
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {otherDocs.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                aria-expanded={showAll}
                className="flex w-full items-center gap-1.5 px-4 py-2.5 text-left text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-700/50"
              >
                {showAll ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                All documents ({otherDocs.length})
              </button>
              {showAll && (
                <ul className="divide-y divide-gray-100 pb-1 dark:divide-gray-700">
                  {otherDocs.map((doc) => (
                    <li key={doc.id} className="flex items-center gap-3 px-4 py-2">
                      <FileText className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-gray-900 dark:text-gray-100">{doc.document_name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{doc.document_type} · v{doc.version_number}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => view(doc.id)}
                        disabled={viewingId === doc.id}
                        aria-label={`View ${doc.document_name}`}
                        className={iconBtnCls}
                      >
                        {viewingId === doc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
